import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, TouchableHighlight, TouchableOpacity, Keyboard, } from 'react-native';
import { AppView, rem, Spacer, withAnimatedNavigationHeader } from 'design-system';
import { Avatar } from 'design-system/avatar';
import ListEmptyView from 'design-system/ListEmptyView';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import SearchBar from 'design-system/SearchBar';
import SectionHeader from 'design-system/SectionHeader';
import Text from 'design-system/Text';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useColors from 'hooks/useColors';
import { useKeyboard } from 'hooks/useKeyboard';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Links from 'utils/links';
import Modal from 'utils/packages/Modal';
import { strcmp } from 'utils/sort';
import ListLoader from 'design-system/ListLoader';
import { KeyboardAwareSectionList } from 'utils/packages/rnKeyboardAwareScrollview';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppSelector } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import { transferProductString } from '../constants';
import { isWeb } from '../../../constants';
import { selectTransferProviders } from '../reducers/selectors';
import { amplitudeSource } from '../utils/analytics';
const strings = {
    title: 'Who are you transferring from?',
    subTitle: (t, type) => t === 'JISA'
        ? 'Search for the provider you’re transferring your child’s Junior ISA from'
        : `Search for the provider you’re transferring your ${transferProductString[type]} ${t} from`,
    myProviderIsNotHere: 'My provider is not here',
    myProviderIsNotHereJISA: 'My child’s provider is not here',
    notSupportedModalTitle: 'Sorry to hear that 😔',
    notSupportedModalSubtitle: 'If your provider isn’t listed, it means we don’t support it just yet. You can still choose another provider, or follow updates in our community forum.',
    notSupportedModalSubtitleJisa: 'If your child’s provider isn’t listed, it means we don’t support it just yet. You can still choose another provider, or follow updates in our community forum.',
    okayThanks: 'Okay, thanks',
    visitCommunity: 'Visit community',
    mostPopular: 'Most popular',
    other: 'Other',
    noResults: 'No results found',
    noResultsSubtitle: 'Make sure you have typed the name correctly or scroll through the list.',
};
const fuseOptions = {
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.4,
    keys: ['officialName', 'shortName'],
};
function displayName(provider) {
    return provider.shortName || provider.officialName;
}
function buildLetterSections(items) {
    const sorted = items
        .map((item) => ({ orig: item, sortKey: displayName(item).normalize('NFD').toUpperCase() }))
        .sort((a, b) => strcmp(a.sortKey, b.sortKey));
    const result = [];
    let searchStart = 0;
    let letterRange = null;
    for (const letter of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
        let index = searchStart;
        for (; index < sorted.length; index += 1) {
            if (sorted[index].sortKey[0] === letter) {
                break;
            }
        }
        if (index < sorted.length) {
            const startIndex = index;
            for (index += 1; index < sorted.length; index += 1) {
                if (sorted[index].sortKey[0] !== letter) {
                    break;
                }
            }
            const endIndex = index;
            result.push({
                title: letter,
                data: sorted.slice(startIndex, endIndex).map(({ orig }) => orig),
            });
            letterRange = letterRange ? [letterRange[0], endIndex] : [startIndex, endIndex];
            searchStart = endIndex;
            if (searchStart === sorted.length) {
                break;
            }
        }
    }
    const other = letterRange ? sorted.slice(0, letterRange[0]).concat(sorted.slice(letterRange[1])) : sorted;
    if (other.length) {
        result.push({
            title: strings.other,
            data: other.map(({ orig }) => orig),
        });
    }
    return result;
}
const InfoHeader = React.memo(({ accountType, type }) => {
    const showMyProviderNotHereModal = useCallback(() => {
        Keyboard.dismiss();
        Modal.showConfirmation({
            title: strings.notSupportedModalTitle,
            subtitle: accountType === 'JISA' ? strings.notSupportedModalSubtitleJisa : strings.notSupportedModalSubtitle,
            confirmButtonTitle: strings.okayThanks,
            cancelButtonTitle: strings.visitCommunity,
            onPressCancel: Links.openEmmaCommunity,
        });
    }, [accountType]);
    return (<ScreenInfoHeader title={strings.title} subTitle={<>
            <Text TextThin-16 Secondary>
              {strings.subTitle(accountType, type)}
            </Text>
            <Spacer h={8}/>
            <View style={styles.row}>
              <TouchableOpacity onPress={showMyProviderNotHereModal}>
                <Text Text-16 Primary>
                  {accountType === 'JISA' ? strings.myProviderIsNotHereJISA : strings.myProviderIsNotHere}
                </Text>
              </TouchableOpacity>
            </View>
          </>}/>);
});
const ListSectionHeader = ({ section }) => (<SectionHeader horizontalMargins largeVerticalMargins>
    {section.title}
  </SectionHeader>);
const ListEmptyComponent = React.memo(({ fetching }) => fetching ? (<ListLoader smallMargin/>) : (<ListEmptyView onCard alt happy style={styles.listEmpty} title={strings.noResults} subTitle={strings.noResultsSubtitle}/>));
function keyExtractor(item) {
    return item.id;
}
const POPULAR_MAX_COUNT = 8;
const ProductTransferSearchProviderScreen = withAnimatedNavigationHeader(({ navigation, route, scrollY }) => {
    const colors = useColors();
    const { type, accountId } = route.params;
    const accountType = accountId ? 'JISA' : 'ISA';
    useEffect(() => {
        Amplitude.logEvent('Transfer Provider Selection Screen', {
            source: amplitudeSource,
            account: accountId ? 'jisa' : 'isa',
        });
    }, [accountId]);
    const [searchTerm, setSearchTerm] = useState('');
    const [headerInfoHeight, setHeaderInfoHeight] = useState(0);
    const scrollRef = useRef(null);
    const onHeaderInfoLayout = useCallback((event) => {
        setHeaderInfoHeight(event.nativeEvent.layout.height - rem(16));
    }, []);
    const { providers, isFetching } = useAppSelector((state) => selectTransferProviders(state, accountType, type));
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.text.primary]);
    const fuse = useMemo(() => new Fuse(providers, fuseOptions), [providers]);
    const onSearchBarFocus = useCallback(() => {
        scrollRef.current?.scrollToPosition(0, headerInfoHeight);
    }, [headerInfoHeight]);
    const ListHeaderComponent = useMemo(() => (<View>
          <View onLayout={onHeaderInfoLayout}>
            <InfoHeader accountType={accountType} type={type}/>
          </View>
          <SearchBar onFocus={onSearchBarFocus} containerStyle={styles.searchBar} onChangeSearchTerm={setSearchTerm} textFieldBackgroundColor={colors.cards.onDark}/>
        </View>), [onHeaderInfoLayout, accountType, type, onSearchBarFocus, colors.cards.onDark]);
    const onPressProvider = useCallback((item) => {
        navigation.navigate('ProductTransferConfirmProvider', { type, provider: item, accountId });
    }, [accountId, type]);
    const sections = useMemo(() => {
        const result = [];
        const popularData = providers.slice(0, POPULAR_MAX_COUNT);
        if (popularData.length) {
            result.push({
                title: strings.mostPopular,
                keyExtractor: (item) => `popular-${item.id}`,
                data: popularData,
            });
        }
        result.push(...buildLetterSections(providers));
        return result;
    }, [providers]);
    const filteredSections = useMemo(() => {
        const trimmed = searchTerm.trim();
        if (!trimmed)
            return sections;
        const results = fuse
            .search(trimmed)
            .filter(({ score }) => (score ?? 1) < 0.5)
            .map(({ item }) => item);
        return buildLetterSections(results);
    }, [searchTerm, sections, fuse]);
    const onScroll = useCallback((event) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = event.nativeEvent.contentOffset.y;
    }, [scrollY]);
    const renderItem = useCallback(({ item, index, section }) => (<TouchableHighlight style={styles.itemTouchable} underlayColor={colors.background.underlay} onPress={() => onPressProvider(item)}>
          <View style={[
            styles.itemContainer,
            { backgroundColor: colors.cards.onDark },
            index === 0 && styles.itemFirst,
            index === section.data.length - 1 && styles.itemLast,
        ]}>
            <Avatar imageUrl={item.imgUrl} name={displayName(item)} color={colors.background.brandLight} fontColor={colors.text.textOnBrand} size={44}/>
            <Spacer.W16 />
            <View style={styles.itemTextContainer}>
              <Text Text-16 numberOfLines={1}>
                {displayName(item)}
              </Text>
            </View>
          </View>
        </TouchableHighlight>), [colors, onPressProvider]);
    const { keyboardHeight } = useKeyboard();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', keyboardHeight, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
        <KeyboardAwareSectionList ref={scrollRef} onScroll={onScroll} sections={filteredSections} keyExtractor={keyExtractor} keyboardDismissMode="on-drag" renderItem={renderItem} keyboardShouldPersistTaps="handled" stickySectionHeadersEnabled={false} contentContainerStyle={paddingBottom} ListHeaderComponent={ListHeaderComponent} renderSectionHeader={ListSectionHeader} ListEmptyComponent={<ListEmptyComponent fetching={isFetching}/>}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default ProductTransferSearchProviderScreen;
const styles = StyleSheet.create({
    searchBar: {
        marginTop: rem(-16),
        paddingBottom: 0,
    },
    itemTouchable: {
        marginHorizontal: rem(16),
    },
    itemContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
    },
    itemFirst: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
    },
    itemLast: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
    },
    listEmpty: {
        marginTop: rem(16),
        marginBottom: 0,
    },
    row: {
        flexDirection: 'row',
    },
    itemTextContainer: {
        flex: 1,
        justifyContent: 'center',
    },
});
